import * as React from 'react';
import axios from 'axios';

export type LocalizationDict_t = { [key: string]: string };

//-----------------------------------------------------------------------------
// Purpose: Singleton localization manager
//-----------------------------------------------------------------------------
export class CLocalizationManager
{
	private static s_Date: Date = new Date();

	private m_mapTokens = new Map<string, string>();
	private m_mapFallbackTokens = new Map<string, string>();

	constructor()
	{
	}

	public InitFromObjects( vecDictionaries: LocalizationDict_t[], vecSharedDictionaries: LocalizationDict_t[], vecFallbackDictionaries: LocalizationDict_t[], vecSharedFallbackDictionaries: LocalizationDict_t[] ): void
	{
		this.m_mapTokens.clear();

		// Merge all primary dictionaries
		let vecDictionaryGroups = [vecSharedDictionaries, vecDictionaries, vecSharedFallbackDictionaries, vecFallbackDictionaries];
		for ( let dictionaryGroupKey in vecDictionaryGroups )
		{
			let vecDictionaries = vecDictionaryGroups[dictionaryGroupKey];
			for ( let dictKey in vecDictionaries )
			{
				let dict = vecDictionaries[dictKey];
				for ( let key in dict )
				{
					let keyIndex = key.toLowerCase();
					if ( !this.m_mapTokens.has( keyIndex ) )
						this.m_mapTokens.set( keyIndex, dict[key] );
				}
			}
		}
	}

	public LocalizeString( strToken: string ): string
	{
		// must start with #
		if ( !strToken || strToken.length == 0 || strToken.charAt( 0 ) != '#' )
			return '';

		let strValue = this.m_mapTokens.get( strToken.substring( 1 ).toLowerCase() );
		if ( strValue === undefined )
			return '';

		return strValue;
	}

	public LocalizeStringFromFallback( strToken: string ): string
	{
		// must start with #
		if ( !strToken || strToken.length == 0 || strToken.charAt( 0 ) != '#' )
			return '';

		let strValue = this.m_mapFallbackTokens.get( strToken.substring( 1 ).toLowerCase() );
		if ( strValue === undefined )
			return '';

		return strValue;
	}

	public static GetLocale(): string
	{
		const sFallbackLocale: string = navigator.languages[0];

		try
		{
			const sLocale: string = VRHTML?.GetSystemLocale();
			if ( !sLocale )
			{
				return sFallbackLocale;
			}

			// Intl.getCanonicalLocales(); is not supported in typescript, which
			// would be a more appropriate test with builtin fixups
			let sTest: string = CLocalizationManager.s_Date.toLocaleTimeString( sLocale );

			return sLocale;
		}
		catch ( e )
		{
			return sFallbackLocale;
		}
	}
}


//-----------------------------------------------------------------------------
// Purpose: Localizes token
// Parameters:	strToken - token to look up, starting with #
//				Variable number of trailing arguments which will be used to replace %1$s, %2$s, etc. in strings
//-----------------------------------------------------------------------------
export function Localize( strToken: string, ...args: ( string | number )[] ): string
{
	let strValue = LocalizationManager.LocalizeString( strToken );
	if ( !strValue )
		return strToken;

	// found a matching token
	if ( args.length > 0 )
	{
		strValue = strValue.replace( /%(\d+)\$s/g, function ( match, index )
		{
			// Subtract one from index because loc strings are 1-based and
			// the args array is 0-based.
			if ( typeof args[index - 1] != 'undefined' )
				return String( args[index - 1] );

			return match;
		} );
	}

	return strValue;
}


//-----------------------------------------------------------------------------
// Purpose: Localizes token, substituting strings or react elements for arguments
// Parameters:	strToken - token to look up, starting with #
//				Variable number of trailing arguments which will be used to replace %1$s, %2$s, etc. in strings
//
//	The output is expected to be used in JSX, eg: <div>{ LocalizeReact( '#foo', <MyReactElement /> ) }</div>
//-----------------------------------------------------------------------------
export function LocalizeReact( strToken: string, ...args: ( React.ReactNode )[] ): React.ReactNode
{
	let strValue = LocalizationManager.LocalizeString( strToken );
	if ( !strValue )
		return strToken;

	let output: React.ReactNode[] = [];

	let regex = /(.*?)%(\d+)\$s/g;
	let cCharactersConsumed = 0;
	let match: RegExpExecArray;
	while ( match = regex.exec( strValue ) )
	{
		cCharactersConsumed += match[0].length;
		output.push( match[1] );

		let index = parseInt( match[2] );
		if ( index >= 1 && index <= args.length )
			output.push( args[index - 1] );
	}

	output.push( strValue.substr( cCharactersConsumed ) );

	return React.createElement( React.Fragment, null, ...output );
}


//-----------------------------------------------------------------------------
// Purpose: Localizes token with a number - expects token to be the singular number case, will look
//		for a token with "_Plural" if number is not 1
// Parameters:	strToken - token to look up, starting with #
//				A number, which will replace %1$s in the resulting string.  Can be a string (formatted number)
//				Variable number of trailing arguments which will be used to replace %2$s, %3$s etc. in strings
//-----------------------------------------------------------------------------
export function LocalizePlural( strToken: string, number: number | string, ...args: ( string | number )[] ): string
{
	if ( number === 1 || number === '1' )
	{
		return Localize( strToken, number, ...args );
	}
	else
	{
		return Localize( strToken + '_Plural', number, ...args );
	}
}


//-----------------------------------------------------------------------------
// Purpose: Localizes token from fallback language (english)
// Parameters:	strToken - token to look up, starting with #
//				Variable number of trailing arguments which will be used to replace %1$s, %2$s, etc. in strings
//-----------------------------------------------------------------------------
export function LocalizeFromFallback( strToken: string, ...args: ( string | number )[] ): string
{
	let strValue = LocalizationManager.LocalizeStringFromFallback( strToken );
	if ( !strValue )
		return strToken;

	// found a matching token
	if ( args.length > 0 )
	{
		strValue = strValue.replace( /%(\d+)\$s/g, function ( match, index )
		{
			// Subtract one from index because loc strings are 1-based and
			// the args array is 0-based.
			if ( typeof args[index - 1] != 'undefined' )
				return String( args[index - 1] );

			return match;
		} );
	}

	return strValue;
}

export const LocalizationManager = new CLocalizationManager();
declare var window: any;
window.LocalizationManager = LocalizationManager;


/**
 * If we didn't get a language from steam, lets peek navigator.languages  and
 * hope we find something good there
 */
function GetBrowserLanguage(): string
{
	let languageMap: Map<string, string> = new Map( [
		["en", "english"],
		["de", "german"],
		["fr", "french"],
		["it", "italian"],
		["ko", "korean"],
		["es-419", "latam"],
		["es", "spanish"],
		["zh-CN", "schinese"],
		["zh-TW", "tchinese"],
		["ru", "russian"],
		["th", "thai"],
		["ja", "japanese"],
		["pt", "portuguese"],
		["pl", "polish"],
		["da", "danish"],
		["nl", "dutch"],
		["fi", "finnish"],
		["no", "norwegian"],
		["sv", "swedish"],
		["hu", "hungarian"],
		["cs", "czech"],
		["ro", "romanian"],
		["tr", "turkish"],
		["pt-BR", "brazilian"],
		["bg", "bulgarian"],
		["el", "greek"],
		//["ar", "arabic"], // Unsupported
		["uk", "ukranian"],
		["vi", "vietnamese"],
	] );

	for ( let language of navigator.languages )
	{
		let components = language.split( '-' );
		if ( languageMap.has( language ) )
			return languageMap.get( language );

		if ( languageMap.has( components[0] ) )
			return languageMap.get( components[0] );
	}

	// If all else fails, just return english
	return "english";
}

export function InitLocalization( vecFiles: string[], strLanguage?: string ): Promise<void>
{
	if ( !strLanguage )
	{
		strLanguage = GetBrowserLanguage();
	}

	let rgPromises: Array<PromiseLike<void>> = [];

	let fnGetDictionary = ( type: string, language: string, fnAssignDict: ( dict: LocalizationDict_t ) => void ) =>
	{

		let file: string;
		let sNow = Date.now().toString();

		// Webhelper is shared, use an absolute path.
		if ( type == 'webhelper' )
			file = `/dashboard/localization/${type}_${language}.json?t=${sNow}`;
		else
			file = `localization/${type}_${language}.json?t=${sNow}`;

		return axios.get( file ).then( ( response ) =>
		{

			fnAssignDict( response.data as LocalizationDict_t );
		} ).catch( () => { } );
	};

	let vecDictionaries: LocalizationDict_t[] = [];
	let vecSharedDictionaries: LocalizationDict_t[] = [];
	let vecFallbackDictionaries: LocalizationDict_t[] = [];
	let vecSharedFallbackDictionaries: LocalizationDict_t[] = [];

	for ( let strFile of vecFiles )
	{
		rgPromises.push( fnGetDictionary( strFile, strLanguage, ( dict ) => { vecDictionaries.push( dict ); } ) );

		if ( strLanguage != 'english' )
		{
			rgPromises.push( fnGetDictionary( strFile, 'english', ( dict ) => { vecFallbackDictionaries.push( dict ); } ) );
		}
	}

	// Also load shared files
	for ( let strFile of ['webhelper'] )
	{
		rgPromises.push( fnGetDictionary( strFile, strLanguage, ( dict ) => { vecSharedDictionaries.push( dict ); } ) );

		if ( strLanguage != 'english' )
		{
			rgPromises.push( fnGetDictionary( strFile, 'english', ( dict ) => { vecSharedFallbackDictionaries.push( dict ); } ) );
		}
	}

	return Promise.all( rgPromises ).then( () =>
	{
		// these should have been assigned in the closures above
		LocalizationManager.InitFromObjects( vecDictionaries, vecSharedDictionaries, vecFallbackDictionaries, vecSharedFallbackDictionaries );
	} );
}
